import { createGlobalState } from '@vueuse/core';
import { computed, reactive, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MessageApi from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';
import {
  calculateHistoryDiffs,
  MessageEditRecord,
  MessageSnapshot,
  MessageEditDiffItem,
} from 'dashboard/helper/messageHistoryHelper';

interface MessageEditHistoryState {
  isOpen: boolean;
  isLoading: boolean;
  messageId: number | null;
  conversationId: number | null;
  history: MessageEditRecord[];
  currentSnapshot: MessageSnapshot | null;
}

export const useMessageEditHistory = createGlobalState(() => {
  const { t } = useI18n();

  const state = reactive<MessageEditHistoryState>({
    isOpen: false,
    isLoading: false,
    messageId: null,
    conversationId: null,
    history: [],
    currentSnapshot: null,
  });

  const openModal = (
    messageId: number,
    conversationId: number,
    currentSnapshot: MessageSnapshot
  ) => {
    state.messageId = messageId;
    state.conversationId = conversationId;
    state.currentSnapshot = currentSnapshot;
    state.isOpen = true;
  };

  const closeModal = () => {
    state.isOpen = false;
    state.messageId = null;
    state.conversationId = null;
    state.currentSnapshot = null;
  };

  const fetchEditHistory = async (cId: number, mId: number) => {
    state.isLoading = true;
    try {
      const { data } = await MessageApi.getMessageEdits(cId, mId);

      if (mId === state.messageId && state.isOpen) {
        if (Array.isArray(data)) {
          state.history = data as MessageEditRecord[];
        } else {
          state.history = [];
        }
      }
    } catch (error) {
      if (mId === state.messageId && state.isOpen) {
        useAlert(t('CONVERSATION.EDIT_HISTORY.FETCH_FAILED'));
        state.history = []; // Сбрасываем историю при ошибке
      }
    } finally {
      if (mId === state.messageId) {
        state.isLoading = false;
      }
    }
  };

  watch([() => state.isOpen, () => state.messageId], ([isOpen, messageId]) => {
    if (isOpen && messageId && state.conversationId) {
      fetchEditHistory(state.conversationId, messageId);
    } else if (!isOpen) {
      state.history = [];
    }
  });

  const diffItems = computed<MessageEditDiffItem[]>(() => {
    return calculateHistoryDiffs(state.history, state.currentSnapshot);
  });

  return {
    ...toRefs(state),
    diffItems,
    openModal,
    closeModal,
  };
});
